import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { fetchWallboxHistoryDataBasedOnView, HistoryPayloadBasedOnView } from '../api'
import { WallboxInfoArray } from '../models'
import { fromWallboxHistoryToRechartData } from '../transformers/from-wallbox-history-to-rechart-data.transformer'
import { WallboxHistoryChartData } from '../models/history-data'
import { usePortalQueries } from 'src/Libs/api'

interface ReturnType {
  wallboxData: WallboxHistoryChartData[]
  isLoading: boolean
}

/**
 * This hook fetches wallbox data. It uses a wallbox step route which is an alternative to the default wallbox route.
 * The route has the potential to be more performant.
 *
 * @param storageId
 * @param wallboxes - an installation can have multiple wallboxes and all of them are relevant for fetching data
 * @param payload
 * @returns
 */
export const useFetchWalloxHistoryViewData = (storageId: string, wallboxes: WallboxInfoArray[], payload: HistoryPayloadBasedOnView): ReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { view } = payload

  const wallboxQueries = wallboxes.map(wallbox => ({
    queryKey: ['wallbox-data', storageId, wallbox.number, payload],
    queryFn: () => fetchWallboxHistoryDataBasedOnView(storageId, wallbox.number.toString(), authFetch, payload),
    enabled: !!storageId,
    refetchOnWindowFocus: true,
  }))

  const wallboxDataResult = usePortalQueries(wallboxQueries)
  const isLoading = wallboxDataResult.some(result => result.isLoading)

  const factor = view === 'day' ? 4 : 1
  let wallboxDataMap = new Map<number, WallboxHistoryChartData>()

  Object.entries(wallboxDataResult).forEach(([key, result]) => {
    if (result.data) {
      wallboxDataMap = fromWallboxHistoryToRechartData(result.data ?? [], factor, wallboxDataMap, wallboxes[Number(key)].number)
    }
  })

  const wallboxData: WallboxHistoryChartData[] = Array.from(wallboxDataMap.values())

  return { wallboxData, isLoading }
}

interface ReturnTypeFetchOnDemand {
  handleFetchingWallboxHistoryData: (wallboxes: WallboxInfoArray[], payload: HistoryPayloadBasedOnView) => Promise<WallboxHistoryChartData[]>
  isFetching: boolean
}

/**
 * Alternative hook to useFetchWalloxHistoryViewData. Instead of fetching data automatically it exposes a function to fetch data manually.
 * @param storageId
 * @param wallboxes
 * @param payload
 * @returns
 */
export const useFetchWallboxHistoryViewDataManual = (storageId: string): ReturnTypeFetchOnDemand => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const isFetching = useIsFetching({ queryKey: ['wallbox-data-manual'] }) > 0

  const handleFetchingWallboxHistoryData = async (
    wallboxes: WallboxInfoArray[],
    payload: HistoryPayloadBasedOnView
  ): Promise<WallboxHistoryChartData[]> => {
    const factor = payload.view === 'day' ? 4 : 1
    let wallboxDataMap = new Map<number, WallboxHistoryChartData>()

    const results = await Promise.allSettled(
      wallboxes.map(wallbox =>
        queryClient.fetchQuery({
          queryKey: ['wallbox-data-manual', storageId, wallbox.number, payload],
          queryFn: () => fetchWallboxHistoryDataBasedOnView(storageId, wallbox.number.toString(), authFetch, payload),
        })
      )
    )

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const wallbox = wallboxes[index]
        wallboxDataMap = fromWallboxHistoryToRechartData(result.value ?? [], factor, wallboxDataMap, wallbox.number)
      }
    })

    return Array.from(wallboxDataMap.values())
  }

  return { handleFetchingWallboxHistoryData, isFetching }
}
